import { addDoc, collection, serverTimestamp } from 'firebase/firestore';

import { db } from '~/config/firebase.config';

export type FeedbackInput = {
    /** 1..5 stars */
    rating: number;
    /** Optional free-text, only collected for ratings <= 3 */
    comment?: string;
    /** The game that was just played (context for the rating) */
    gameID?: string;
    deviceID?: string;
    uid?: string;
    sessionID?: string;
    mode?: string;
};

/**
 * Player satisfaction feedback collected on the gamepad between games.
 *
 * Written to the TOP-LEVEL `userFeedback` collection (with `clientID` as a field)
 * so the super-admin dashboard can aggregate every review with a single
 * `orderBy('date')` read instead of fanning out one query per ~824 clients.
 *
 * The gamepad is an anonymous guest, so the Firestore rule for `userFeedback`
 * allows `create` for unauthenticated writers as long as the payload matches the
 * exact field set + constraints below (rating int 1..5, comment string <= 2000)
 * — see the `userFeedback` match block in firestore.rules.
 *
 * NB: previously written under `clients/{id}/public/feedback/items`, which the
 * dashboard never read — those reviews were silently orphaned.
 */
const getFeedbackRef = () => collection(db, 'userFeedback');

export const FeedbackService = {
    async saveFeedback(clientID: string, input: FeedbackInput): Promise<string> {
        const ref = getFeedbackRef();
        const doc = {
            clientID,
            rating: input.rating,
            comment: input.comment ?? null,
            gameID: input.gameID ?? null,
            deviceID: input.deviceID ?? null,
            uid: input.uid ?? null,
            sessionID: input.sessionID ?? null,
            mode: input.mode ?? null,
            date: serverTimestamp(),
        };
        const created = await addDoc(ref, doc);
        return created.id;
    },
};
